import { until } from 'until-async'
import { getCacheBustedUrl, initCacheBusters } from '../CacheBuster'
import { loadScript } from '../robowhale/net/load-script'
import { assertNever } from '../robowhale/utils/assert-never'
import { NetUtil } from '../robowhale/utils/NetUtil'
import { UrlParams } from '../url-params/UrlParams'
import { initGameConfig } from './init-game-config'
import { loadGameTexts } from './load-game-texts'
import { BasePlatformPreloader } from './platform/BasePlatformPreloader'
import { PlaygamaPlatformPreloader } from './platform/PlaygamaPlatformPreloader'
import { VkPlatformPreloader } from './platform/VkPlatformPreloader'
import { YaPlatformPreloader } from './platform/YaPlatformPreloader'

export type PreloaderLogger = (level: 'log' | 'warn', channel: string, message: string, ...args: unknown[]) => void

export class Preloader {
	public platform: BasePlatformPreloader | undefined

	constructor() {
		console.clear()

		if (document.readyState === 'complete') {
			this.start()
		} else {
			window.addEventListener('load', () => this.start())
		}
	}

	private async start() {
		await this.loadErudaConsole()

		window.game = {}

		const logger = this.log.bind(this)

		logger('log', 'preloader', 'url params', UrlParams.getAll())

		// game config is already loaded at this point via index.html
		initGameConfig()

		// cache busters are loaded at this point via index.html
		initCacheBusters()

		// some platforms require to init as soon possible thats why we load and init it here in Preloader
		this.loadPlatformScript()

		await loadGameTexts(logger)

		await this.loadGameScript()
	}

	private loadErudaConsole() {
		let erudaValue = NetUtil.getParamInt('console')
		if (erudaValue <= 0) {
			return
		}

		return new Promise<void>((resolve, reject) => {
			let body = document.getElementsByTagName('body')[0]
			let script = document.createElement('script')
			script.src = '//cdn.jsdelivr.net/npm/eruda'
			script.type = 'text/javascript'

			script.onerror = () => {
				reject(new Error('Eruda console failed to load'))
			}

			script.onload = () => {
				this.onErudaConsoleLoad(erudaValue)
				resolve()
			}

			body.appendChild(script)
		})
	}

	private onErudaConsoleLoad(value: number) {
		eruda.init({
			tool: ['console', 'elements', 'info'],
		})

		if (value === 2) {
			eruda.show()
		}
	}

	private async loadPlatformScript() {
		const platform = window.game.config.platform

		switch (platform) {
			case 'dummy':
			case 'aeroflot':
				return

			case 'yandex':
				this.platform = new YaPlatformPreloader()
				this.platform.loadAndInit()
				return

			case 'vk':
				this.platform = new VkPlatformPreloader()
				this.platform.loadAndInit()
				return

			case 'playgama':
				this.platform = new PlaygamaPlatformPreloader()
				this.platform.loadAndInit()
				return

			default:
				assertNever(platform, 'Unknown platform')
		}
	}

	private async loadGameScript() {
		const gameScriptUrl =
			window.game.config.build_type === 'dev' ? 'js/game.js' : getCacheBustedUrl('js/game.min.js')

		await this.loadScriptWithRetries(gameScriptUrl)
	}

	protected async loadScriptWithRetries(url: string, maxAttempts = 3, attempt = 1): Promise<void> {
		this.log('log', 'preloader', `load#${attempt} - start (${url})`)

		const [error] = await until(() => loadScript(url))
		if (!error) {
			this.log('log', 'preloader', `load#${attempt} - complete`)
			return
		}

		this.log(
			'warn',
			'preloader',
			`load#${attempt} - failed (${error instanceof Error ? error.message : 'unknown error'})`
		)

		const isLastAttempt = attempt === maxAttempts
		if (isLastAttempt) {
			throw error instanceof Error ? error : new Error('script failed to load')
		}

		return this.loadScriptWithRetries(url, maxAttempts, attempt + 1)
	}

	private log(level: 'log' | 'warn', channel: string, message: string, ...args: unknown[]) {
		const timestamp = this.getLogTimestamp()

		console[level](`[${timestamp}] [${channel}] ${message}`, ...args)
	}

	private getLogTimestamp(): string {
		const date = new Date()

		const hours = date.getHours().toString().padStart(2, '0')
		const minutes = date.getMinutes().toString().padStart(2, '0')
		const seconds = date.getSeconds().toString().padStart(2, '0')
		const milliseconds = date.getMilliseconds().toString().padStart(3, '0')

		return `${hours}:${minutes}:${seconds}.${milliseconds}`
	}
}

window['preloader'] = new Preloader()
